// src/components/AddCompanyForm/CepRangesCard.jsx
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Typography,
  Box,
  IconButton,
  Switch,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Checkbox,
  TablePagination,
  Chip,
  FormControlLabel,
  Tooltip,
  Paper,
} from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { onlyDigits, validCep8, slugify, uid } from "./utils";
import {
  auditCepRanges,
  findActiveOverlaps,
  findCompanyByCep,
} from "./cepRangeAudit";
import { previewCepImport } from "./cepRangeImport";
import CepCoverageMapDialog from "./CepCoverageMapDialog";
import CepUnattendedDialog from "./CepUnattendedDialog";
import ConcessionDiffDialog from "./ConcessionDiffDialog";
import { fetchData } from "../../api/client";

const FINDING_LABEL = {
  overlap_conflict: "Conflito",
  overlap_redundant: "Redundante",
  invalid: "Inválida",
  orphan_region: "Marca órfã",
  orphan_target: "Destino órfão",
  inactive_overlap: "Inativa",
};

const FINDING_COLOR = {
  block: "error",
  warn: "warning",
  info: "info",
};

function rangeKey(r) {
  return r._id != null ? String(r._id) : `idx-${r._idx}`;
}

function matchesAuditRow(unused, row, idx) {
  if (unused._id != null && row._id != null && String(unused._id) === String(row._id)) return true;
  if (unused._id == null && unused._idx != null && unused._idx === idx) return true;
  return (
    String(unused.region_key || "") === String(row.region_key || "") &&
    onlyDigits(unused.start_cep) === onlyDigits(row.start_cep) &&
    onlyDigits(unused.end_cep) === onlyDigits(row.end_cep) &&
    String(unused.target_cod_interno || "") === String(row.target_cod_interno || "")
  );
}

function sameAuditRange(a, b) {
  if (!a || !b) return false;
  if (a._id != null && b._id != null) return String(a._id) === String(b._id);
  return (
    String(a.region_key || "") === String(b.region_key || "") &&
    onlyDigits(a.start_cep) === onlyDigits(b.start_cep) &&
    onlyDigits(a.end_cep) === onlyDigits(b.end_cep) &&
    String(a.target_cod_interno || "") === String(b.target_cod_interno || "")
  );
}

function isOverlapFinding(f) {
  return f?.type === "overlap_conflict" || f?.type === "overlap_redundant";
}

function uniqueUnused(findings) {
  const list = [];
  const seen = new Set();
  for (const f of findings || []) {
    if (!isOverlapFinding(f) || f.severity !== "block") continue;
    for (const u of f.unused || []) {
      const k = `${rangeKey(u)}|${u.region_key}|${onlyDigits(u.start_cep)}|${onlyDigits(u.end_cep)}|${u.target_cod_interno}`;
      if (seen.has(k)) continue;
      seen.add(k);
      list.push(u);
    }
  }
  return list;
}

function Section({ title, hint, icon, children, tone = "default", action }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        bgcolor: tone === "muted" ? "action.hover" : "background.paper",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: hint ? 0.5 : 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle2">{title}</Typography>
        </Stack>
        {action || null}
      </Stack>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
          {hint}
        </Typography>
      ) : null}
      {children}
    </Paper>
  );
}

const CepRangesCard = forwardRef(function CepRangesCard(
  { companyId, regionKeys = [], cepRanges = [], setCepRanges, targetSlugsByRegion, onTargetSlugsByRegion },
  ref
) {
  const [newRange, setNewRange] = useState({
    _id: uid(),
    region_key: "",
    start_cep: "",
    end_cep: "",
    target_cod_interno: "",
    status: 1,
  });

  const [feedback, setFeedback] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importRegion, setImportRegion] = useState("");
  const [importTarget, setImportTarget] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [auditOpen, setAuditOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [unattendedOpen, setUnattendedOpen] = useState(false);
  const [concessionOpen, setConcessionOpen] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [prodReport, setProdReport] = useState(null);
  const [auditTab, setAuditTab] = useState("form");
  const [auditFilter, setAuditFilter] = useState("block");

  const [simulateCep, setSimulateCep] = useState("");
  const [simulateResult, setSimulateResult] = useState(null);

  const [filterMarca, setFilterMarca] = useState("");
  const [filterDestino, setFilterDestino] = useState("");
  const [filterCep, setFilterCep] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterConflicts, setFilterConflicts] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const clearFeedbackLater = (ms = 6000) => {
    window.clearTimeout(clearFeedbackLater._t);
    clearFeedbackLater._t = window.setTimeout(() => setFeedback(null), ms);
  };

  const localReport = useMemo(
    () =>
      auditCepRanges(cepRanges, {
        regionKeys,
        targetSlugsByRegion:
          targetSlugsByRegion && typeof targetSlugsByRegion === "object" ? targetSlugsByRegion : undefined,
      }),
    [cepRanges, regionKeys, targetSlugsByRegion]
  );

  const conflictKeys = useMemo(() => {
    const s = new Set();
    for (const f of localReport.findings || []) {
      if (f.severity !== "block") continue;
      for (const r of f.ranges || []) s.add(rangeKey(r));
    }
    return s;
  }, [localReport]);

  const runLocalAudit = () =>
    auditCepRanges(cepRanges, {
      regionKeys,
      targetSlugsByRegion:
        targetSlugsByRegion && typeof targetSlugsByRegion === "object" ? targetSlugsByRegion : undefined,
    });

  const openAudit = async () => {
    setFeedback(null);
    setAuditTab("form");
    const local = runLocalAudit();
    setAuditFilter(local.hasBlocking ? "block" : "all");
    setAuditOpen(true);
    if (!companyId) return;
    setAuditBusy(true);
    try {
      const data = await fetchData(`/companies/${companyId}/cep-audit`, "GET");
      const report = data?.data && !Array.isArray(data.data) ? data.data : data;
      setProdReport(report || null);
      if (report?.targetSlugsByRegion && typeof report.targetSlugsByRegion === "object") {
        onTargetSlugsByRegion?.(report.targetSlugsByRegion);
      }
    } catch (err) {
      setProdReport({
        fetchError: err?.message || "Falha ao carregar auditoria gravada.",
        findings: [],
        summary: {},
        hasBlocking: false,
      });
    } finally {
      setAuditBusy(false);
    }
  };

  useImperativeHandle(ref, () => ({ openAudit, runLocalAudit }));

  const addRange = () => {
    setFeedback(null);
    const region_key = String(newRange.region_key || "").trim();
    const start_cep = onlyDigits(newRange.start_cep);
    const end_cep = onlyDigits(newRange.end_cep);
    const target = slugify(newRange.target_cod_interno || "");

    if (!region_key || !validCep8(start_cep) || !validCep8(end_cep) || !target) {
      setFeedback({
        type: "error",
        message: "Preencha marca, CEP inicial (8 díg.), CEP final (8 díg.) e destino (slug do projeto).",
      });
      clearFeedbackLater();
      return;
    }
    if (Number(start_cep) > Number(end_cep)) {
      setFeedback({ type: "error", message: "CEP inicial não pode ser maior que o CEP final." });
      clearFeedbackLater();
      return;
    }

    const overlaps = findActiveOverlaps(
      { region_key, start_cep, end_cep, target_cod_interno: target, status: 1 },
      cepRanges
    );
    if (overlaps.length) {
      setFeedback({
        type: "error",
        message: `Sobreposição com faixa ativa ${overlaps[0].region_key} ${overlaps[0].start_cep}–${overlaps[0].end_cep} → ${overlaps[0].target_cod_interno}.`,
      });
      clearFeedbackLater(8000);
      return;
    }

    setCepRanges((prev) => [
      ...prev,
      {
        _id: uid(),
        region_key,
        start_cep,
        end_cep,
        target_cod_interno: target,
        status: 1,
        ordem: prev.length,
      },
    ]);

    setNewRange({
      _id: uid(),
      region_key: "",
      start_cep: "",
      end_cep: "",
      target_cod_interno: "",
      status: 1,
    });

    setFeedback({ type: "success", message: "Faixa adicionada." });
    clearFeedbackLater(3000);
  };

  const removeRange = (id) => setCepRanges((p) => p.filter((r) => r._id !== id));

  const toggleRangeStatus = (id) =>
    setCepRanges((prev) => prev.map((r) => (r._id === id ? { ...r, status: r.status === 1 ? 0 : 1 } : r)));

  const patchRange = (id, patch) =>
    setCepRanges((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const onRangeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRange();
    }
  };

  const openImport = () => {
    setFeedback(null);
    setImportRegion("");
    setImportTarget("");
    setImportFile(null);
    setImportPreview(null);
    setImportOpen(true);
  };
  const closeImport = () => {
    if (!importBusy) setImportOpen(false);
  };

  const handlePickFile = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = "";
    setImportFile(f);
    setImportPreview(null);
  };

  const handleDownloadTemplate = () => {
    const headers = ["CEP Inicial", "CEP Final"];
    const sample = [
      ["88000000", "88009999"],
      ["88010000", "88019999"],
    ];
    const csv = [headers.join(";"), ...sample.map((row) => row.join(";"))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exemplo-faixas-cep.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImportPreview = async () => {
    try {
      setFeedback(null);
      const region_key = String(importRegion || "").trim();
      const target = slugify(importTarget || "");
      if (!importFile) {
        setFeedback({ type: "error", message: "Selecione um arquivo CSV para importar." });
        clearFeedbackLater();
        return;
      }
      setImportBusy(true);
      const text = await importFile.text();
      const preview = previewCepImport({
        text,
        defaultRegion: region_key,
        defaultTarget: target,
        existingRanges: cepRanges,
      });
      setImportPreview(preview);
      if (preview.headerError) {
        setFeedback({ type: "error", message: preview.headerError });
        clearFeedbackLater();
      }
    } catch (err) {
      setFeedback({
        type: "error",
        message: `Falha ao ler o arquivo: ${err?.message || "erro inesperado"}.`,
      });
      clearFeedbackLater(8000);
    } finally {
      setImportBusy(false);
    }
  };

  const applyImportPreview = () => {
    const next = importPreview?.toAdd || [];
    if (!next.length) return;
    setCepRanges((prev) => {
      const base = prev.length;
      return [...prev, ...next.map((r, idx) => ({ ...r, _id: uid(), ordem: base + idx }))];
    });
    const added = next.length;
    const blocked = importPreview.overlapBlocked?.length || 0;
    const dupes = importPreview.duplicatesSkipped || 0;
    const errs = importPreview.errors?.length || 0;
    setFeedback({
      type: blocked || errs ? "warning" : "success",
      message: `Importação: ${added} adicionada(s), ${dupes} duplicata(s) ignorada(s), ${blocked} bloqueada(s) por sobreposição, ${errs} inválida(s).`,
    });
    clearFeedbackLater(10000);
    setImportOpen(false);
    setImportPreview(null);
    const newTotal = cepRanges.length + added;
    const lastPage = Math.max(0, Math.ceil(newTotal / rowsPerPage) - 1);
    setPage(lastPage);
  };

  const onSimulate = () => {
    const d = onlyDigits(simulateCep);
    if (!validCep8(d)) {
      setSimulateResult({ type: "error", message: "Informe um CEP com 8 dígitos." });
      return;
    }
    const hit = findCompanyByCep(d, cepRanges);
    if (!hit) {
      setSimulateResult({
        type: "info",
        message: `CEP ${d}: sem cobertura nas faixas ativas deste cadastro (o e-commerce responderia served: false).`,
      });
      return;
    }
    setSimulateResult({
      type: "success",
      message: `CEP ${d}: marca ${hit.regionKey} → destino ${hit.company} (faixa ${hit.range.start}–${hit.range.end}). Esta é a regra first-match do e-commerce.`,
    });
  };

  const filteredRanges = useMemo(() => {
    const cepQ = onlyDigits(filterCep);
    const destQ = String(filterDestino || "").trim().toLowerCase();
    return (cepRanges || []).filter((r, idx) => {
      if (filterMarca && r.region_key !== filterMarca) return false;
      if (destQ && !String(r.target_cod_interno || "").toLowerCase().includes(destQ)) return false;
      if (filterStatus === "on" && Number(r.status) !== 1) return false;
      if (filterStatus === "off" && Number(r.status) === 1) return false;
      if (cepQ) {
        if (cepQ.length === 8) {
          const n = Number(cepQ);
          const a = Number(onlyDigits(r.start_cep));
          const b = Number(onlyDigits(r.end_cep));
          if (!(Number.isFinite(n) && n >= a && n <= b)) return false;
        } else if (
          !onlyDigits(r.start_cep).includes(cepQ) &&
          !onlyDigits(r.end_cep).includes(cepQ)
        ) {
          return false;
        }
      }
      if (filterConflicts) {
        const key = r._id != null ? String(r._id) : `idx-${idx}`;
        if (!conflictKeys.has(key) && !conflictKeys.has(`idx-${idx}`)) return false;
      }
      return true;
    });
  }, [cepRanges, filterMarca, filterDestino, filterCep, filterStatus, filterConflicts, conflictKeys]);

  const total = filteredRanges.length;
  const pageCount = Math.ceil(total / rowsPerPage) || 1;
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * rowsPerPage;
  const end = Math.min(start + rowsPerPage, total);
  const pageRows = filteredRanges.slice(start, end);

  const isSelected = (id) => selectedIds.has(id);
  const toggleRowSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectCurrentPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) {
        if (checked) next.add(r._id);
        else next.delete(r._id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkActivate = () => {
    if (selectedIds.size === 0) return;
    setCepRanges((prev) => prev.map((r) => (selectedIds.has(r._id) ? { ...r, status: 1 } : r)));
  };
  const bulkDeactivate = () => {
    if (selectedIds.size === 0) return;
    setCepRanges((prev) => prev.map((r) => (selectedIds.has(r._id) ? { ...r, status: 0 } : r)));
  };
  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    const toRemove = selectedIds.size;
    setCepRanges((prev) => prev.filter((r) => !selectedIds.has(r._id)));
    setSelectedIds(new Set());
    const newTotal = total - toRemove;
    const newPageCount = Math.ceil(Math.max(newTotal, 0) / rowsPerPage) || 1;
    if (page >= newPageCount) setPage(Math.max(0, newPageCount - 1));
  };

  const deleteUnusedRows = (unusedList) => {
    if (!unusedList?.length) return 0;
    setCepRanges((prev) => prev.filter((row, idx) => !unusedList.some((u) => matchesAuditRow(u, row, idx))));
    const ids = unusedList.map((u) => u._id).filter((id) => id != null);
    if (ids.length) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }
    setAuditTab("form");
    return unusedList.length;
  };

  const viewConflictRanges = (f) => {
    const sample = (f.unused && f.unused[0]) || (f.ranges && f.ranges[0]);
    const cep = onlyDigits(f.intersection?.start || sample?.start_cep || "").slice(0, 8);
    if (cep) setFilterCep(cep);
    setFilterMarca("");
    setFilterDestino("");
    setFilterStatus("on");
    setFilterConflicts(true);
    const unusedIds = (f.unused || []).map((u) => u._id).filter((id) => id != null);
    if (unusedIds.length) setSelectedIds(new Set(unusedIds));
    setPage(0);
    setAuditOpen(false);
    setFeedback({
      type: "info",
      message: "Mostrando as faixas deste conflito na tabela. Exclusões só entram em vigor ao salvar.",
    });
    clearFeedbackLater(8000);
  };

  const unusedCollective = uniqueUnused(localReport.findings);

  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r._id));
  const somePageSelected = pageRows.some((r) => selectedIds.has(r._id));

  const filtersActive = Boolean(
    filterMarca || filterDestino || filterCep || filterStatus !== "all" || filterConflicts
  );
  const clearFilters = () => {
    setFilterMarca("");
    setFilterDestino("");
    setFilterCep("");
    setFilterStatus("all");
    setFilterConflicts(false);
    setPage(0);
  };
  const showConflictsInTable = () => {
    setFilterStatus("on");
    setFilterConflicts(true);
    setPage(0);
  };

  const renderGlobalFeedback =
    !importOpen && !auditOpen && !mapOpen && !unattendedOpen && !concessionOpen && feedback ? (
      <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
        {feedback.message}
      </Alert>
    ) : null;

  const shownReport = auditTab === "prod" ? prodReport : localReport;
  const shownFindings = (shownReport?.findings || []).filter((f) => {
    if (auditFilter === "block") return f.severity === "block";
    if (auditFilter === "warn") return f.severity !== "block";
    return true;
  });

  return (
    <Card variant="outlined">
      <CardHeader
        title="Faixas de CEP"
        subheader={
          localReport.summary?.blocking
            ? `${cepRanges.length} faixa(s) · ${localReport.summary.blocking} conflito(s) nas ativas`
            : `${cepRanges.length} faixa(s) na cerca virtual deste projeto`
        }
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            <Button size="small" variant="outlined" startIcon={<MapRoundedIcon />} onClick={() => setMapOpen(true)}>
              Mapa
            </Button>
            <Tooltip title="Comparar com a planilha de área de atuação (cidades/concessões)">
              <Button size="small" variant="outlined" onClick={() => setConcessionOpen(true)}>
                Concessões
              </Button>
            </Tooltip>
            <Tooltip title="Cidades tocadas pela cerca com trechos de CEP ainda sem atendimento">
              <Button size="small" variant="outlined" onClick={() => setUnattendedOpen(true)}>
                Não atendidos
              </Button>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<FactCheckRoundedIcon />} onClick={openAudit}>
              Auditar
            </Button>
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {renderGlobalFeedback}

        {localReport.hasBlocking ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={0.5}>
                <Button color="inherit" size="small" onClick={showConflictsInTable}>
                  Mostrar na tabela
                </Button>
                <Button color="inherit" size="small" onClick={openAudit}>
                  Ver conflitos
                </Button>
              </Stack>
            }
          >
            Há faixas ativas se sobrepondo. Corrija antes de salvar — no e-commerce vale a primeira
            marca em ordem A–Z.
          </Alert>
        ) : null}

        <Stack spacing={2}>
          <Section
            tone="muted"
            icon={<SearchRoundedIcon fontSize="small" color="action" />}
            title="Consultar CEP"
            hint="Testa a cerca virtual com a mesma regra do e-commerce. Não grava nada."
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  label="CEP"
                  value={simulateCep}
                  onChange={(e) => setSimulateCep(onlyDigits(e.target.value).slice(0, 8))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSimulate();
                    }
                  }}
                  fullWidth
                  size="small"
                  placeholder="01001000"
                  inputProps={{ maxLength: 8 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button variant="outlined" onClick={onSimulate}>
                  Consultar
                </Button>
              </Grid>
            </Grid>
            {simulateResult ? (
              <Alert severity={simulateResult.type} sx={{ mt: 1.5 }} onClose={() => setSimulateResult(null)}>
                {simulateResult.message}
              </Alert>
            ) : null}
          </Section>

          <Section
            icon={<AddRoundedIcon fontSize="small" color="action" />}
            title="Incluir faixa"
            hint="Uma faixa por vez, com CEP inicial e final (8 dígitos) e o slug da empresa de destino. Para várias de uma vez, importe um CSV."
            action={
              <Button
                size="small"
                variant="text"
                startIcon={<UploadFileRoundedIcon />}
                onClick={openImport}
              >
                Importar CSV
              </Button>
            }
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Marca"
                  select
                  fullWidth
                  size="small"
                  value={newRange.region_key}
                  onChange={(e) => setNewRange((p) => ({ ...p, region_key: e.target.value }))}
                  onKeyDown={onRangeKeyDown}
                >
                  {regionKeys.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="CEP inicial"
                  value={newRange.start_cep}
                  onChange={(e) => setNewRange((p) => ({ ...p, start_cep: onlyDigits(e.target.value) }))}
                  onKeyDown={onRangeKeyDown}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 8 }}
                  placeholder="01001000"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="CEP final"
                  value={newRange.end_cep}
                  onChange={(e) => setNewRange((p) => ({ ...p, end_cep: onlyDigits(e.target.value) }))}
                  onKeyDown={onRangeKeyDown}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 8 }}
                  placeholder="01500999"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Destino (slug do projeto)"
                  value={newRange.target_cod_interno}
                  onChange={(e) => setNewRange((p) => ({ ...p, target_cod_interno: e.target.value }))}
                  onKeyDown={onRangeKeyDown}
                  fullWidth
                  size="small"
                  placeholder="ex.: filial"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" onClick={addRange} fullWidth startIcon={<AddRoundedIcon />}>
                  Adicionar
                </Button>
              </Grid>
            </Grid>
          </Section>

          <Section
            icon={<FilterListRoundedIcon fontSize="small" color="action" />}
            title="Lista de faixas"
            hint={
              cepRanges.length
                ? `Filtros valem só para a tabela. Mostrando ${total} de ${cepRanges.length} faixa(s).`
                : "As faixas cadastradas aparecem aqui."
            }
            action={
              filtersActive ? (
                <Button size="small" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              ) : null
            }
          >
            {cepRanges.length ? (
              <Grid container spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Marca"
                    select
                    fullWidth
                    size="small"
                    value={filterMarca}
                    onChange={(e) => {
                      setFilterMarca(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {regionKeys.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Destino"
                    size="small"
                    fullWidth
                    value={filterDestino}
                    onChange={(e) => {
                      setFilterDestino(e.target.value);
                      setPage(0);
                    }}
                    placeholder="Slug da empresa"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="CEP"
                    size="small"
                    fullWidth
                    value={filterCep}
                    onChange={(e) => {
                      setFilterCep(onlyDigits(e.target.value).slice(0, 8));
                      setPage(0);
                    }}
                    placeholder="Contém ou cobre"
                    helperText={filterCep.length === 8 ? "Faixas que cobrem este CEP" : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <TextField
                    label="Status"
                    select
                    size="small"
                    fullWidth
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="on">Ativos</MenuItem>
                    <MenuItem value="off">Inativos</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterConflicts}
                        onChange={(e) => {
                          setFilterConflicts(e.target.checked);
                          setPage(0);
                        }}
                      />
                    }
                    label="Só conflitos"
                  />
                </Grid>
              </Grid>
            ) : null}

        {selectedIds.size !== 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2">{`${selectedIds.size} registro(s) selecionado(s)`}</Typography>
            <Button variant="contained" startIcon={<CheckCircleRoundedIcon />} onClick={bulkActivate}>
              Ativar selecionados ({selectedIds.size})
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<BlockRoundedIcon />}
              onClick={bulkDeactivate}
            >
              Desativar selecionados ({selectedIds.size})
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={bulkDelete}
            >
              Excluir selecionados ({selectedIds.size})
            </Button>
            <Button variant="text" onClick={clearSelection}>
              Limpar seleção
            </Button>
            <Box flex={1} />
          </Stack>
        )}

        {cepRanges.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma faixa cadastrada. Inclua acima ou importe um CSV.
          </Typography>
        ) : total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma faixa com esses filtros.
            {filtersActive ? (
              <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>
                Limpar filtros
              </Button>
            ) : null}
          </Typography>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["", "Marca", "CEP inicial", "CEP final", "Destino", "Ativo", "Excluir"].map(
                      (h) => (
                        <th
                          key={h || "sel"}
                          style={{
                            borderBottom: "1px solid #e0e0e0",
                            padding: 8,
                            textAlign: "left",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h === "" ? (
                            <Checkbox
                              indeterminate={!allPageSelected && somePageSelected}
                              checked={allPageSelected}
                              onChange={(e) => selectCurrentPage(e.target.checked)}
                              inputProps={{ "aria-label": "Selecionar página" }}
                            />
                          ) : (
                            h
                          )}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const absIdx = cepRanges.findIndex((x) => x._id === r._id);
                    const isConflict =
                      conflictKeys.has(String(r._id)) || conflictKeys.has(`idx-${absIdx}`);
                    return (
                      <tr
                        key={r._id}
                        style={{ background: isConflict ? "rgba(211, 47, 47, 0.08)" : undefined }}
                      >
                        <td style={{ padding: 8 }}>
                          <Checkbox
                            checked={isSelected(r._id)}
                            onChange={() => toggleRowSelect(r._id)}
                            inputProps={{ "aria-label": "Selecionar linha" }}
                          />
                        </td>
                        <td style={{ padding: 8, minWidth: 140 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={r.region_key || ""}
                            onChange={(e) => patchRange(r._id, { region_key: e.target.value })}
                          >
                            {regionKeys.includes(r.region_key) ? null : (
                              <MenuItem value={r.region_key}>{r.region_key || "—"}</MenuItem>
                            )}
                            {regionKeys.map((k) => (
                              <MenuItem key={k} value={k}>
                                {k}
                              </MenuItem>
                            ))}
                          </TextField>
                        </td>
                        <td style={{ padding: 8, minWidth: 110 }}>
                          <TextField
                            size="small"
                            value={onlyDigits(r.start_cep)}
                            onChange={(e) =>
                              patchRange(r._id, { start_cep: onlyDigits(e.target.value).slice(0, 8) })
                            }
                            inputProps={{ maxLength: 8 }}
                          />
                        </td>
                        <td style={{ padding: 8, minWidth: 110 }}>
                          <TextField
                            size="small"
                            value={onlyDigits(r.end_cep)}
                            onChange={(e) =>
                              patchRange(r._id, { end_cep: onlyDigits(e.target.value).slice(0, 8) })
                            }
                            inputProps={{ maxLength: 8 }}
                          />
                        </td>
                        <td style={{ padding: 8, minWidth: 160 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={r.target_cod_interno || ""}
                            onChange={(e) => patchRange(r._id, { target_cod_interno: e.target.value })}
                            onBlur={() =>
                              patchRange(r._id, {
                                target_cod_interno: slugify(r.target_cod_interno || ""),
                              })
                            }
                          />
                        </td>
                        <td style={{ padding: 8 }}>
                          <Switch
                            size="small"
                            checked={Number(r.status) === 1}
                            onChange={() => toggleRangeStatus(r._id)}
                          />
                        </td>
                        <td style={{ padding: 8 }}>
                          <IconButton size="small" onClick={() => removeRange(r._id)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>

            <TablePagination
              component="div"
              count={total}
              page={safePage}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage="Linhas por página"
            />
          </>
        )}
          </Section>
        </Stack>
      </CardContent>

      <Dialog open={importOpen} onClose={closeImport} fullWidth maxWidth="md">
        <DialogTitle>Importar faixas de CEP</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {feedback && importOpen ? (
              <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            ) : null}

            <Typography variant="body2" color="text.secondary">
              Use cabeçalhos <strong>CEP Inicial</strong> e <strong>CEP Final</strong>. Opcionalmente{" "}
              <strong>marca</strong> e <strong>destino</strong> por linha; senão, os campos abaixo valem para o
              arquivo inteiro. Sobreposições com faixas ativas não são importadas.
            </Typography>

            <TextField
              label="Marca (padrão do arquivo)"
              select
              fullWidth
              value={importRegion}
              onChange={(e) => setImportRegion(e.target.value)}
              disabled={importBusy}
            >
              {regionKeys.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Destino padrão (slug do projeto)"
              value={importTarget}
              onChange={(e) => setImportTarget(e.target.value)}
              placeholder="ex.: filial"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              disabled={importBusy}
              fullWidth
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
              <Button
                variant="outlined"
                onClick={handlePickFile}
                disabled={importBusy}
                startIcon={<UploadFileRoundedIcon />}
              >
                Selecionar CSV
              </Button>
              <Typography variant="body2" color="text.secondary" noWrap title={importFile?.name || ""}>
                {importFile?.name || "Nenhum arquivo selecionado"}
              </Typography>
              <Box flex={1} />
              <Button
                variant="text"
                onClick={handleDownloadTemplate}
                disabled={importBusy}
                startIcon={<DownloadRoundedIcon />}
              >
                Baixar exemplo
              </Button>
            </Stack>

            {importPreview && !importPreview.headerError ? (
              <Alert
                severity={
                  importPreview.overlapBlocked.length || importPreview.errors.length ? "warning" : "info"
                }
              >
                {importPreview.toAdd.length} pronta(s) para adicionar
                {importPreview.duplicatesSkipped
                  ? `, ${importPreview.duplicatesSkipped} duplicata(s)`
                  : ""}
                {importPreview.overlapBlocked.length
                  ? `, ${importPreview.overlapBlocked.length} bloqueada(s) por sobreposição`
                  : ""}
                {importPreview.errors.length ? `, ${importPreview.errors.length} inválida(s)` : ""}.
                {importPreview.overlapBlocked.length
                  ? ` Ex.: ${importPreview.overlapBlocked
                      .slice(0, 2)
                      .map((b) => b.message)
                      .join(" | ")}`
                  : ""}
                {importPreview.errors.length
                  ? ` ${importPreview.errors.slice(0, 2).join(" | ")}`
                  : ""}
              </Alert>
            ) : null}

            {importBusy && <LinearProgress />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImport} disabled={importBusy}>
            Fechar
          </Button>
          <Button onClick={runImportPreview} variant="outlined" disabled={importBusy}>
            Analisar
          </Button>
          <Button
            onClick={applyImportPreview}
            variant="contained"
            disabled={importBusy || !importPreview?.toAdd?.length}
          >
            Importar {importPreview?.toAdd?.length ? `(${importPreview.toAdd.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Auditoria de faixas de CEP</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Qual versão das faixas auditar
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={auditTab === "form" ? "contained" : "outlined"}
                onClick={() => setAuditTab("form")}
              >
                Edição desta tela
              </Button>
              <Tooltip
                title={
                  companyId
                    ? "Faixas já salvas no banco — é o que o e-commerce usa hoje."
                    : "Salve a empresa para comparar com a versão publicada."
                }
              >
                <span>
                  <Button
                    size="small"
                    variant={auditTab === "prod" ? "contained" : "outlined"}
                    onClick={() => setAuditTab("prod")}
                    disabled={!companyId}
                  >
                    Já salvo (site)
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {auditTab === "prod"
                ? "Conflitos da cerca publicada: o que já está gravado e o e-commerce usa hoje."
                : "Conflitos do rascunho: inclui inclusões, exclusões e importações ainda não salvas."}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant={auditFilter === "block" ? "contained" : "outlined"}
                color="error"
                onClick={() => setAuditFilter("block")}
              >
                Bloqueios ({shownReport?.summary?.blocking ?? 0})
              </Button>
              <Button
                size="small"
                variant={auditFilter === "warn" ? "contained" : "outlined"}
                color="warning"
                onClick={() => setAuditFilter("warn")}
              >
                Avisos ({(shownReport?.summary?.warn || 0) + (shownReport?.summary?.info || 0)})
              </Button>
              <Button
                size="small"
                variant={auditFilter === "all" ? "contained" : "outlined"}
                onClick={() => setAuditFilter("all")}
              >
                Todos
              </Button>
            </Stack>
            {auditBusy && auditTab === "prod" ? <LinearProgress /> : null}
            {auditOpen && feedback ? (
              <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            ) : null}
            {shownReport?.fetchError ? (
              <Alert severity="error">{shownReport.fetchError}</Alert>
            ) : (
              <>
                <Typography variant="body2">
                  {shownReport?.summary?.total ?? 0} faixa(s), {shownReport?.summary?.active ?? 0} ativa(s),{" "}
                  {shownReport?.summary?.blocking ?? 0} sobreposição(ões), {shownReport?.summary?.warn ?? 0} aviso(s).
                  Destino é empresa no workspace da marca, não neste cadastro central.
                </Typography>
                {auditTab === "form" && unusedCollective.length ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {unusedCollective.length} faixa(s) perdedora(s) totalmente coberta(s) pelo vencedor — o
                      e-commerce nunca as usa.
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteOutlineRoundedIcon />}
                      onClick={() => {
                        const n = unusedCollective.length;
                        deleteUnusedRows(unusedCollective);
                        setFeedback({
                          type: "warning",
                          message: `${n} faixa(s) não usada(s) removida(s) deste cadastro. Clique em Salvar para gravar.`,
                        });
                        clearFeedbackLater(8000);
                      }}
                    >
                      Excluir todas as não usadas
                    </Button>
                  </Stack>
                ) : null}
                {!(shownFindings || []).length ? (
                  <Alert severity="success">
                    {auditFilter === "block" && (shownReport?.summary?.blocking || 0) === 0
                      ? "Nenhuma sobreposição de faixas ativas."
                      : "Nenhum item neste filtro."}
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {shownFindings.map((f, idx) => (
                      <Alert
                        key={`${f.type}-${idx}`}
                        severity={FINDING_COLOR[f.severity] || "info"}
                        icon={false}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={FINDING_LABEL[f.type] || f.type}
                            color={FINDING_COLOR[f.severity] || "default"}
                          />
                          {f.winner ? (
                            <Typography variant="caption">
                              Vencedor: {f.winner.region_key} → {f.winner.target_cod_interno}
                            </Typography>
                          ) : null}
                          {f.count > 1 ? (
                            <Typography variant="caption">{f.count} faixas</Typography>
                          ) : null}
                        </Stack>
                        <Typography variant="body2">{f.message}</Typography>
                        {(f.ranges || []).map((row, j) => {
                          const overlap = isOverlapFinding(f);
                          const isWinner = overlap && sameAuditRange(f.winner, row);
                          const isUnused = overlap && (f.unused || []).some((u) => sameAuditRange(u, row));
                          const isPartial =
                            overlap && (f.unusedPartial || []).some((u) => sameAuditRange(u, row));
                          return (
                            <Typography key={j} variant="caption" display="block">
                              {row.region_key} {row.start_cep}–{row.end_cep} → {row.target_cod_interno}
                              {isWinner ? " · usada no e-commerce" : ""}
                              {isUnused ? " · não usada (coberta pelo vencedor)" : ""}
                              {isPartial ? " · parcial (ainda cobre CEPs fora da sobreposição)" : ""}
                              {Number(row.status) === 1 ? "" : " (inativa)"}
                            </Typography>
                          );
                        })}
                        {isOverlapFinding(f) && auditTab === "form" ? (
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityOutlinedIcon />}
                              onClick={() => viewConflictRanges(f)}
                            >
                              Ver na tabela
                            </Button>
                            {(f.unused || []).length ? (
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                onClick={() => {
                                  const n = f.unused.length;
                                  deleteUnusedRows(f.unused);
                                  setFeedback({
                                    type: "warning",
                                    message: `${n} faixa(s) não usada(s) removida(s) (${f.unused
                                      .map((u) => `${u.region_key} → ${u.target_cod_interno}`)
                                      .join(", ")}). Clique em Salvar para gravar.`,
                                  });
                                  clearFeedbackLater(8000);
                                }}
                              >
                                {f.unused.length > 1 ? "Excluir não usadas" : "Excluir não usada"}
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                A faixa perdedora ainda atende CEPs fora da interseção; não exclua sem revisar.
                              </Typography>
                            )}
                          </Stack>
                        ) : null}
                      </Alert>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <CepCoverageMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        companyId={companyId}
        cepRanges={cepRanges}
        onViewCep={(cep, overlap) => {
          const digits = onlyDigits(cep).slice(0, 8);
          if (digits) setFilterCep(digits);
          setFilterMarca("");
          setFilterDestino("");
          setFilterStatus("on");
          setFilterConflicts(Boolean(overlap));
          setPage(0);
          setMapOpen(false);
          setFeedback({
            type: "info",
            message: "Mostrando na tabela as faixas do município selecionado no mapa.",
          });
          clearFeedbackLater(8000);
        }}
      />
      <CepUnattendedDialog
        open={unattendedOpen}
        onClose={() => setUnattendedOpen(false)}
        companyId={companyId}
        cepRanges={cepRanges}
        onViewCep={(cep) => {
          const digits = onlyDigits(cep).slice(0, 8);
          if (digits) setFilterCep(digits);
          setFilterMarca("");
          setFilterDestino("");
          setFilterStatus("all");
          setFilterConflicts(false);
          setPage(0);
          setUnattendedOpen(false);
          setFeedback({
            type: "info",
            message: "Filtro da tabela no CEP inicial da faixa não atendida.",
          });
          clearFeedbackLater(8000);
        }}
      />
      <ConcessionDiffDialog
        open={concessionOpen}
        onClose={() => setConcessionOpen(false)}
        regionKeys={regionKeys}
        cepRanges={cepRanges}
        targetSlugsByRegion={targetSlugsByRegion}
        onTargetSlugsByRegion={onTargetSlugsByRegion}
        onApplyRanges={(ranges) => {
          setCepRanges(ranges);
          setConcessionOpen(false);
          setPage(0);
          setFeedback({
            type: "success",
            message: `Faixas da planilha aplicadas no cadastro (${ranges.length} faixa(s), sem sobreposição). Revise a tabela e salve a empresa para publicar no e-commerce.`,
          });
          clearFeedbackLater(12000);
        }}
      />
    </Card>
  );
});

export default CepRangesCard;
